import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'bootstrap';
import { useCounters } from '../../contexts/CounterContext';
import { useToast } from '../../utils/toastUtils';
import ToastContainer from '../common/ToastContainer';
import CreateCounterModal from '../modals/CreateCounterModal';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

function CounterNotFound() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addCounter } = useCounters();
  const { toasts, showToast } = useToast();
  const [isCreating, setIsCreating] = useState(false);
  const modalRef = useRef<Modal | null>(null);

  // Set document title
  useDocumentTitle(
    t('errors.notFound', 'Tap Counter Not Found'),
    'TapDonkey',
    t('errors.notFoundMessage', 'Tap counter not found - TapDonkey')
  );

  const openCreateModal = () => {
    const modalEl = document.getElementById('createCounterModal');
    if (modalEl) {
      const modal = new Modal(modalEl);
      modalRef.current = modal;
      modal.show();
    }
  };

  const handleCreateCounter = async (name: string, isPrivate?: boolean, email?: string) => {
    try {
      setIsCreating(true);
      const newCounter = await addCounter(name, isPrivate, email);
      
      // Hide the modal
      if (modalRef.current) {
        modalRef.current.hide();
      }
      
      // Navigate to the new counter
      navigate(`/${newCounter.ownerUrl}`);
    } catch (err) {
      console.error('Failed to create counter:', err);
      showToast(t('errors.failedToCreate', 'Failed to create counter'), 'danger');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm bg-body-tertiary">
            <div className="card-body text-center p-5">
              <div className="mb-4">
                <div className="d-inline-flex bg-warning bg-opacity-10 text-warning p-3 rounded-circle">
                  <i className="bi bi-exclamation-triangle-fill display-4"></i>
                </div>
              </div>
              
              <h1 className="h3 fw-bold mb-3">{t('errors.notFound', 'Tap Counter Not Found')}</h1>
              <p className="mb-4 text-body-secondary">
                {t('errors.notFoundMessage', 'The tap counter you are looking for does not exist or has been deleted.')}
              </p>
              
              <div className="d-grid">
                <button 
                  className="btn btn-primary"
                  onClick={openCreateModal}
                >
                  <i className="bi bi-plus-circle me-2"></i>
                  {t('counters.createNew', 'New TapTracker')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Counter Modal */}
      <CreateCounterModal 
        isCreating={isCreating} 
        onCreateCounter={handleCreateCounter}
      />
      
      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} title={t('errors.notFound', 'Tap Counter Not Found')} />
    </div>
  );
}

export default CounterNotFound; 